import { translate } from '@jsverse/transloco';

import { WidgetChartOptions } from 'app/modules/widget/components/widget-chart/widget-chart.component';
import { Dashboard, Widget } from 'app/providers/bridge-api/dashboard.service';
import { FieldService } from 'app/providers/storage-api/field.service';
import { Report } from 'app/providers/storage-api/report.service';
import { StringBuilder } from 'app/utils/string-builder';
import { Utils } from 'app/utils/utils';
import { AbstractChartReport } from '../chart-report-abstract/chart-report-abstract';
import { ChartDataService } from '../../chart-data.service';
import { HighchartsOptions } from '../../chart-display.service';

interface GaugeItems {
  metric: { [key: string]: any };
  gaugeBox?: { [key: string]: any };
  gaugeRating?: { [key: string]: any };
}

export class GaugeChartReport extends AbstractChartReport {
  public reportName = 'documentNumericMetadata';
  public reportHandler = (dashboard, widget, response, _options) =>
    ChartDataService.forGauge(dashboard, widget, response, this.comparisonService, this.options);

  public canExportAsPptx = false;

  public getFields(): string[] {
    return [];
  }

  public canHideItems(): boolean {
    return false;
  }

  public canDrilldown(): boolean {
    return false;
  }

  public applyComparisonToSeries(comparison, series, _seriesIdx) {
    series.custom ??= {};
    series.custom.comparisonNames = comparison.filters.map((f) => f.label);
  }

  public async executeReport(
    format: 'json' | 'csv',
    passedQuery: string
  ): Promise<string | Report<{ [key: string]: any }>> {
    const params = await super.getParams();

    const body = this.getBody();
    const numericMetadataResponse = (await this.reportService.postReport(
      this.options.widgetDataSource.collectionName,
      this.options.widgetDataSource.groupId,
      this.reportName,
      params,
      body,
      'json',
      passedQuery,
      undefined,
      undefined,
      this.options.projectId
    )) as Report<{ [key: string]: any }>;

    this.lastDocumentCount = numericMetadataResponse.totalHits;

    const items: GaugeItems = {
      metric: numericMetadataResponse.items.data.metadata[0],
    };

    const response = {
      ...numericMetadataResponse,
      items: {
        ...items,
      },
    };

    if (format === 'csv') {
      return this.formatToCsv(items);
    }

    return response;
  }

  private getBody() {
    const body = [];
    const gaugeOptions = this.options.gaugeOptions;

    const metricBody = {
      customField: this.options.analyticItem,
    };

    switch (gaugeOptions.metric) {
      case 'avg':
        metricBody['scoringStrategy'] = 'AVERAGE';
        break;
      case 'box':
        {
          metricBody['scoringStrategy'] = 'BOX';
          metricBody['scoreRange'] = `${gaugeOptions.minRating}:${gaugeOptions.maxRating}`;
          metricBody['boxDirection'] = gaugeOptions.metricBoxType.toLocaleUpperCase();
          metricBody['boxSize'] = gaugeOptions.metricBoxSize;
        }
        break;
      case 'nps':
        metricBody['scoringStrategy'] = 'NPS';
        metricBody['scoreRange'] = `${gaugeOptions.minRating}:${gaugeOptions.maxRating}`;
        break;
    }
    body.push(metricBody);

    return body;
  }

  private formatToCsv(items: GaugeItems): string {
    const sb = new StringBuilder();
    sb.appendLine('metric,element,score,method');
    const tableRows = GaugeChartReportUtils.toTable(items, this.options);
    tableRows.forEach((r) => sb.appendLine(`${r.metric},${r.element},${r.score},${r.method}`));
    return sb.toString();
  }

  public getHighchartsOptions(
    dashboard: Dashboard,
    widget: Widget<WidgetChartOptions>,
    response: any,
    options: HighchartsOptions
  ): HighchartsOptions {
    const metric = FieldService.getFieldLongName(response.items.metric.field);
    const ret = super.getHighchartsOptions(dashboard, widget, response, options);
    const score = Number(options.series[0]?.data[0]?.value);
    if (response.comparisonBuckets) {
      const comparisonNameExtractor = () => {
        const comparison = dashboard.comparisons[widget.payload.comparison];
        const sb = new StringBuilder();
        const extractor = this.comparisonService.filterDisplayValueExtractorFn(comparison?.type);
        comparison?.filters.forEach((fs) => {
          if (sb.length) {
            sb.append(' VS: ');
          }
          sb.append(extractor(fs));
        });
        return sb.toString();
      };
      ret.tooltip.headerFormat = `${comparisonNameExtractor()}`;
      ret.tooltip.pointFormatter = () => '';
    } else {
      ret.tooltip.headerFormat = `${translate('widgets.charting.tooltip.name-part', {
        name: metric,
      })}<br/>`;
      if (score) {
        ret.tooltip.pointFormatter = function () {
          return `${translate('widgets.charting.tooltip.score-part', {
            score: Utils.addThousandSeparator(
              Utils.round(score, widget.payload.gaugeOptions.decimalPrecision ?? 2)
            ),
          })}${widget.payload.gaugeOptions.metric === 'box' ? '%' : ''}`;
        };
      }
    }

    return ret;
  }
}

interface GaugeTableRow {
  metric: string;
  element: string;
  score: number;
  method: string;
}

export class GaugeChartReportUtils {
  static toTable(items: GaugeItems, payload: WidgetChartOptions): GaugeTableRow[] {
    const result: GaugeTableRow[] = [];

    const gaugeOptions = payload.gaugeOptions;

    const metric = FieldService.getFieldLongName(payload.analyticItem);
    let innerScore: number;
    const metricData = items.metric;
    if (gaugeOptions.metric === 'box') {
      if (gaugeOptions.metricBoxType === 'top') {
        innerScore = metricData.topScore;
      } else {
        innerScore = metricData.bottomScore;
      }
    } else {
      innerScore = metricData.score;
    }

    let innerMethod: string;
    switch (gaugeOptions.metric) {
      case 'avg':
        innerMethod = 'Average';
        break;
      case 'box':
        innerMethod = gaugeOptions.metricBoxType === 'top' ? 'TopBox' : 'BottomBox';
        break;
      case 'nps':
        innerMethod = 'NPS';
        break;
    }
    result.push({
      metric,
      element: translate('widgets.widget-add.gauge'),
      score: Number(innerScore),
      method: innerMethod,
    });

    return result;
  }
}
